import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Trophy, RefreshCw, Play, X, Plus, Trash2 } from 'lucide-react';
import api from '../services/api';
import useMarkAsReadOnMount from '../hooks/useMarkAsReadOnMount';
import './Users.css';
import './TasksRewards.css';

const Rewards = () => {
  useMarkAsReadOnMount(['reward']);

  const now = new Date();
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [report, setReport] = useState(null);

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Simulation state
  const [showSimulation, setShowSimulation] = useState(false);
  const [simPool, setSimPool] = useState(10000);
  const [simMonth, setSimMonth] = useState('April 2026');
  const [simDq, setSimDq] = useState(60);
  const [simContributors, setSimContributors] = useState([
    { name: 'Alice', quota: 12, low: 3, medium: 3, high: 2, urgent: 1, attendance: 96 },
    { name: 'Yassine', quota: 10, low: 2, medium: 2, high: 2, urgent: 0, attendance: 84 },
    { name: 'Meryem', quota: 11, low: 4, medium: 2, high: 1, urgent: 0, attendance: 58 },
  ]);
  const [simResults, setSimResults] = useState(null);

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.items)) return value.items;
    if (Array.isArray(value?.records)) return value.records;
    if (Array.isArray(value?.users)) return value.users;
    return [];
  };

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const projectsRes = await api.get('/projects');
      const records = normalizeArray(projectsRes);
      setProjects(records);

      if (!records.length) {
        setSelectedProjectId('');
        setReport(null);
        setMessage({ type: 'error', text: 'No projects available for rewards reporting.' });
        return;
      }

      setSelectedProjectId((prev) => {
        if (prev && records.some((p) => String(p.id) === String(prev))) return prev;
        return String(records[0].id);
      });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to load projects.' });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadReport = useCallback(async () => {
    if (!selectedProjectId) {
      setReport(null);
      return;
    }

    setLoading(true);
    try {
      const data = await api.get(
        `/projects/${selectedProjectId}/monthly-report?month=${Number(month)}&year=${Number(year)}`,
      );
      setReport(data || null);
      setMessage({ type: '', text: '' });
    } catch (err) {
      setReport(null);
      setMessage({ type: 'error', text: err.message || 'Failed to load project monthly report.' });
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId, month, year]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const contributors = useMemo(() => report?.contributors || [], [report]);
  const totalEstimatedReward = contributors.reduce((sum, c) => sum + Number(c.estimatedReward || 0), 0);
  const maxEstimatedReward = useMemo(
    () => Math.max(1, ...contributors.map((c) => Number(c.estimatedReward || 0))),
    [contributors],
  );

  const [attendanceData, setAttendanceData] = useState({});

  // Calculate attendance rate from attendance records (same logic as MyAttendance)
  const calculateAttendanceRate = (records, currentMonth, currentYear) => {
    const monthRecords = records.filter(r => {
      const dateStr = r.date;
      const recordMonth = parseInt(dateStr.substring(5, 7), 10);
      const recordYear = parseInt(dateStr.substring(0, 4), 10);
      return recordMonth === currentMonth && recordYear === currentYear;
    });

    const isLeaveRecord = (record) => {
      const status = String(record?.status || '').toLowerCase();
      const notes = String(record?.notes || '').toLowerCase();
      return status === 'leave' || notes.includes('leave');
    };

    // Calculate workdays in the month (excluding weekends)
    const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
    let workdays = 0;
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth - 1, day);
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Exclude Saturday (6) and Sunday (0)
        workdays++;
      }
    }

    // Get authorized leave days (deduplicate by date)
    const authorizedLeaveDays = new Set(monthRecords.filter(r => isLeaveRecord(r)).map(r => r.date)).size;

    // Total expected workdays = workdays - authorized leaves
    const totalExpectedDays = workdays - authorizedLeaveDays;

    // Present days (deduplicate by date so remote + regular on same day count once)
    const presentLikeDays = new Set(monthRecords.filter(r =>
      !isLeaveRecord(r) && ['present', 'late'].includes(String(r.status).toLowerCase())
    ).map(r => r.date)).size;

    const rate = totalExpectedDays > 0 ? ((presentLikeDays / totalExpectedDays) * 100).toFixed(1) : '0.0';
    return rate;
  };

  // Fetch attendance data for all contributors (same sources as MyAttendance)
  const fetchAttendanceForContributors = async () => {
    if (!contributors.length) return;

    const normalizeArray = (value) => {
      if (Array.isArray(value)) return value;
      if (Array.isArray(value?.records)) return value.records;
      if (Array.isArray(value?.items)) return value.items;
      if (Array.isArray(value?.history)) return value.history;
      return [];
    };

    const normalizeDate = (value) => {
      if (!value) return '';
      if (typeof value === 'string') return value.slice(0, 10);
      return new Date(value).toISOString().slice(0, 10);
    };

    const enumerateDates = (startDate, endDate) => {
      const dates = [];
      const cursor = new Date(`${startDate}T00:00:00Z`);
      const end = new Date(`${endDate}T00:00:00Z`);
      while (cursor <= end) {
        dates.push(cursor.toISOString().slice(0, 10));
        cursor.setUTCDate(cursor.getUTCDate() + 1);
      }
      return dates;
    };

    const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
    const monthEnd = `${year}-${String(month).padStart(2, '0')}-31`;

    const attendancePromises = contributors.map(async (contributor) => {
      try {
        const [regularRes, remoteRes, absencesRes] = await Promise.allSettled([
          api.get(`/attendance/user/${contributor.userId}`),
          api.get(`/attendance/remote/history/${contributor.userId}?startDate=${monthStart}&endDate=${monthEnd}`),
          api.get(`/absences/user/${contributor.userId}`),
        ]);

        let records = [];

        if (regularRes.status === 'fulfilled') {
          const regularRecords = normalizeArray(regularRes.value).map((r) => ({
            ...r,
            date: normalizeDate(r.date),
          }));
          records = [...records, ...regularRecords.filter((r) => !r.notes?.includes('Method: remote'))];
        }

        if (remoteRes.status === 'fulfilled') {
          const remoteData = remoteRes.value || {};
          const remoteHistory = Array.isArray(remoteData)
            ? remoteData
            : (Array.isArray(remoteData.history) ? remoteData.history : []);
          const remoteRecords = remoteHistory.map((r) => ({
            id: r.id,
            date: normalizeDate(r.date),
            clockIn: r.clockIn,
            clockOut: r.clockOut,
            status: ['completed', 'active', 'auto_clocked_out'].includes(r.status) ? 'present' : 'absent',
            notes: `Method: remote, Session: ${r.sessionId}`,
            userId: r.userId,
            isRemote: true,
          }));
          records = [...records, ...remoteRecords];
        }

        if (absencesRes.status === 'fulfilled') {
          const absences = normalizeArray(absencesRes.value)
            .filter((a) => String(a?.status || '').toLowerCase() === 'approved');

          absences.forEach((absence) => {
            const start = normalizeDate(absence.startDate);
            const end = normalizeDate(absence.endDate);
            if (!start || !end) return;
            enumerateDates(start, end).forEach((date) => {
              const alreadyExists = records.some((r) => r.date === date && String(r.status).toLowerCase() === 'leave');
              if (alreadyExists) return;
              records.push({
                id: `leave-${absence.id}-${date}`,
                date,
                clockIn: null,
                clockOut: null,
                status: 'leave',
                notes: `Leave request #${absence.id}`,
                userId: absence.userId,
                isRemote: false,
              });
            });
          });
        }

        return { userId: contributor.userId, records };
      } catch (error) {
        console.error(`Failed to fetch attendance for user ${contributor.userId}:`, error);
        return { userId: contributor.userId, records: [] };
      }
    });

    const results = await Promise.all(attendancePromises);
    const attendanceMap = {};
    results.forEach(result => {
      attendanceMap[result.userId] = result.records;
    });
    setAttendanceData(attendanceMap);
  };

  useEffect(() => {
    fetchAttendanceForContributors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contributors, month, year]);

  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const getStatusStyle = (entry) => {
    const attendanceRate = parseFloat(calculateAttendanceRate(attendanceData[entry.userId] || [], Number(month), Number(year)));
    
    if (entry.disqualified) {
      return {
        background: 'rgba(239, 68, 68, 0.14)',
        color: '#fca5a5',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        label: 'Disqualified',
      };
    }

    if (attendanceRate >= 95) {
      return {
        background: 'rgba(34, 197, 94, 0.14)',
        color: '#86efac',
        border: '1px solid rgba(34, 197, 94, 0.3)',
        label: 'Excellent',
      };
    }

    if (attendanceRate >= 80) {
      return {
        background: 'rgba(245, 158, 11, 0.14)',
        color: '#fcd34d',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        label: 'Good',
      };
    }

    return {
      background: 'rgba(148, 163, 184, 0.18)',
      color: '#cbd5e1',
      border: '1px solid rgba(148, 163, 184, 0.35)',
      label: 'Normal',
    };
  };

  // Simulation functions
  const WEIGHTS = { low: 1, medium: 1.5, high: 2, urgent: 2 };

  const getMultiplier = (attendance, threshold) => {
    if (attendance < threshold) return 0;
    if (attendance >= 95) return 2;
    if (attendance >= 80) return 1.5;
    return 1;
  };

  const runSimulation = () => {
    const pool = Number(simPool || 0);
    const dq = Number(simDq || 60);

    const rows = simContributors.map((c) => {
      const weightedCompleted =
        c.low * WEIGHTS.low +
        c.medium * WEIGHTS.medium +
        c.high * WEIGHTS.high +
        c.urgent * WEIGHTS.urgent;

      const overQuota = Math.max(0, weightedCompleted - c.quota);
      const multiplier = getMultiplier(c.attendance, dq);
      const finalScore = Number((overQuota * multiplier).toFixed(2));

      return { ...c, weightedCompleted: Number(weightedCompleted.toFixed(2)), overQuota: Number(overQuota.toFixed(2)), multiplier, finalScore };
    });

    const totalScore = rows.reduce((s, r) => s + r.finalScore, 0);

    rows.forEach((r) => {
      r.reward = totalScore > 0 ? Number(((r.finalScore / totalScore) * pool).toFixed(2)) : 0;
    });

    setSimResults({ rows, totalScore, pool, qualified: rows.filter((r) => r.multiplier > 0).length });
  };

  const resetSimulation = () => {
    setSimContributors([
      { name: 'Alice', quota: 12, low: 3, medium: 3, high: 2, urgent: 1, attendance: 96 },
      { name: 'Yassine', quota: 10, low: 2, medium: 2, high: 2, urgent: 0, attendance: 84 },
      { name: 'Meryem', quota: 11, low: 4, medium: 2, high: 1, urgent: 0, attendance: 58 },
    ]);
    runSimulation();
  };

  const addContributor = () => {
    setSimContributors([...simContributors, { name: 'New', quota: 10, low: 0, medium: 0, high: 0, urgent: 0, attendance: 90 }]);
  };

  const removeContributor = (index) => {
    setSimContributors(simContributors.filter((_, i) => i !== index));
  };

  const updateContributor = (index, field, value) => {
    const updated = [...simContributors];
    updated[index][field] = field === 'name' ? value : Number(value);
    setSimContributors(updated);
  };

  const getSimStatusStyle = (attendance, multiplier) => {
    if (multiplier === 0) {
      return { background: 'rgba(239, 68, 68, 0.14)', color: '#fca5a5', border: '1px solid rgba(239, 68, 68, 0.3)', label: 'Disqualified' };
    }
    if (attendance >= 95) {
      return { background: 'rgba(34, 197, 94, 0.14)', color: '#86efac', border: '1px solid rgba(34, 197, 94, 0.3)', label: 'Excellent' };
    }
    if (attendance >= 80) {
      return { background: 'rgba(245, 158, 11, 0.14)', color: '#fcd34d', border: '1px solid rgba(245, 158, 11, 0.3)', label: 'Good' };
    }
    return { background: 'rgba(148, 163, 184, 0.18)', color: '#cbd5e1', border: '1px solid rgba(148, 163, 184, 0.35)', label: 'Normal' };
  };

  if (loading) return <div className="page-loading">Loading rewards...</div>;

  return (
    <div className="users-page">
      <div className="page-header">
        <h1><Trophy size={24} /> Rewards Reporting</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => setShowSimulation(true)}><Play size={14} /> Run Simulation</button>
          <button className="btn btn-secondary" onClick={loadReport}><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      {message.text ? (
        <div className={`message ${message.type}`} style={{ marginBottom: 14 }}>{message.text}</div>
      ) : null}

      <div className="tr-stats-grid">
        <div className="tr-stat-card">
          <div className="tr-stat-label">Total Over-Quota Score</div>
          <div className="tr-stat-value">{Number(report?.totalOverQuotaScore || 0).toFixed(2)}</div>
        </div>
        <div className="tr-stat-card">
          <div className="tr-stat-label">Qualified Contributors</div>
          <div className="tr-stat-value">{Number(report?.qualifiedContributors || 0)}</div>
        </div>
        <div className="tr-stat-card">
          <div className="tr-stat-label">Allocated Reward</div>
          <div className="tr-stat-value">{Number(totalEstimatedReward).toFixed(2)}</div>
        </div>
      </div>

      <div className="users-table-container tr-card" style={{ marginBottom: 16, padding: 16 }}>
        <h3 className="tr-card-header">Project & Month Filters</h3>
        <div className="users-filters tr-form-grid" style={{ gap: 10, gridTemplateColumns: '2fr 1fr 1fr auto' }}>
          <select
            className="tr-select"
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
          >
            <option value="">Select project</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <select className="tr-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {monthOptions.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <input
            className="tr-input"
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
          <button className="btn btn-secondary" onClick={loadReport}><RefreshCw size={14} /> Run</button>
        </div>

        {report ? (
          <div className="tr-summary-line" style={{ marginTop: 10 }}>
            <strong>{report.projectName}</strong> · {monthOptions.find((m) => m.value === Number(month))?.label} {year} · Pool {Number(report.totalRewardPool || 0).toFixed(2)}
          </div>
        ) : null}
      </div>

      <div className="users-table-container tr-card" style={{ padding: 16 }}>
        <div>
          <h3 className="tr-card-header">Monthly Contributor Report</h3>
          <table className="users-table">
            <thead>
              <tr>
                <th>Contributor</th>
                <th>Weighted Done</th>
                <th>Quota</th>
                <th>Over Quota</th>
                <th>Attendance</th>
                <th>Multiplier</th>
                <th>Final Score</th>
                <th>Reward</th>
              </tr>
            </thead>
            <tbody>
              {contributors.length ? contributors.map((c) => {
                const status = getStatusStyle(c);
                return (
                  <tr key={c.userId}>
                    <td>{c.name}</td>
                    <td>{Number(c.weightedCompleted || 0).toFixed(2)}</td>
                    <td>{Number(c.quota || 0).toFixed(2)}</td>
                    <td>{Number(c.overQuotaScore || 0).toFixed(2)}</td>
                    <td>
                      {calculateAttendanceRate(attendanceData[c.userId] || [], Number(month), Number(year))}%{' '}
                      <span
                        style={{
                          ...status,
                          borderRadius: 999,
                          padding: '2px 8px',
                          fontSize: 11,
                          fontWeight: 700,
                          marginLeft: 6,
                        }}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td>{Number(c.multiplier || 0).toFixed(2)}x</td>
                    <td><strong>{Number(c.finalScore || 0).toFixed(2)}</strong></td>
                    <td><strong>{Number(c.estimatedReward || 0).toFixed(2)}</strong></td>
                  </tr>
                );
              }) : (
                <tr><td colSpan="8" className="empty-row">No report data for selected project/month.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 12 }}>
          {contributors.map((c) => {
            const ratio = maxEstimatedReward > 0 ? (Number(c.estimatedReward || 0) / maxEstimatedReward) * 100 : 0;
            return (
              <div key={`bar-${c.userId}`} style={{ display: 'grid', gridTemplateColumns: '140px 1fr 90px', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{c.name}</div>
                <div style={{ height: 12, borderRadius: 999, background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${ratio}%`,
                      background: 'linear-gradient(90deg, #22d3ee, #38bdf8)',
                    }}
                  />
                </div>
                <div style={{ textAlign: 'right', fontWeight: 700 }}>{Number(c.estimatedReward || 0).toFixed(2)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Simulation Modal */}
      {showSimulation && (
        <div className="modal-overlay" onClick={() => setShowSimulation(false)}>
          <div className="modal" style={{ maxWidth: 1100, maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2>Rewards Reporting Simulator</h2>
              <button className="btn-icon" onClick={() => setShowSimulation(false)}><X size={18} /></button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {/* Simulation Inputs */}
              <div className="tr-card" style={{ padding: 14 }}>
                <h3 className="tr-card-header" style={{ marginBottom: 12 }}>Simulation Inputs</h3>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)' }}>Project Reward Pool</label>
                    <input
                      className="tr-input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={simPool}
                      onChange={(e) => setSimPool(Number(e.target.value))}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)' }}>Month</label>
                    <input
                      className="tr-input"
                      type="text"
                      value={simMonth}
                      onChange={(e) => setSimMonth(e.target.value)}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)' }}>Disqualification Threshold (%)</label>
                    <input
                      className="tr-input"
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={simDq}
                      onChange={(e) => setSimDq(Number(e.target.value))}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {simContributors.map((c, idx) => (
                    <div key={idx} className="tr-card" style={{ padding: 10, marginBottom: 0, background: 'var(--bg-tertiary)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <h4 style={{ margin: 0, fontSize: 14, color: 'var(--text-primary)' }}>Contributor {idx + 1}</h4>
                        <button className="btn-icon delete" onClick={() => removeContributor(idx)}><Trash2 size={14} /></button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 8 }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)' }}>Name</label>
                          <input
                            className="tr-input"
                            value={c.name}
                            onChange={(e) => updateContributor(idx, 'name', e.target.value)}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)' }}>Quota (weighted)</label>
                          <input
                            className="tr-input"
                            type="number"
                            min="0"
                            step="0.1"
                            value={c.quota}
                            onChange={(e) => updateContributor(idx, 'quota', e.target.value)}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)' }}>Attendance %</label>
                          <input
                            className="tr-input"
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={c.attendance}
                            onChange={(e) => updateContributor(idx, 'attendance', e.target.value)}
                          />
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 8 }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)' }}>Low tasks (x1)</label>
                          <input
                            className="tr-input"
                            type="number"
                            min="0"
                            step="1"
                            value={c.low}
                            onChange={(e) => updateContributor(idx, 'low', e.target.value)}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)' }}>Medium tasks (x1.5)</label>
                          <input
                            className="tr-input"
                            type="number"
                            min="0"
                            step="1"
                            value={c.medium}
                            onChange={(e) => updateContributor(idx, 'medium', e.target.value)}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)' }}>High tasks (x2)</label>
                          <input
                            className="tr-input"
                            type="number"
                            min="0"
                            step="1"
                            value={c.high}
                            onChange={(e) => updateContributor(idx, 'high', e.target.value)}
                          />
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)' }}>Urgent tasks (x2)</label>
                        <input
                          className="tr-input"
                          type="number"
                          min="0"
                          step="1"
                          value={c.urgent}
                          onChange={(e) => updateContributor(idx, 'urgent', e.target.value)}
                        />
                      </div>
                    </div>
                  ))}

                  <button className="btn btn-secondary" onClick={addContributor} style={{ marginTop: 8 }}>
                    <Plus size={14} /> Add Contributor
                  </button>

                  <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                    <button className="btn btn-primary" onClick={runSimulation}>Run Simulation</button>
                    <button className="btn btn-secondary" onClick={resetSimulation}>Load Example Data</button>
                  </div>
                </div>
              </div>

              {/* Simulation Results */}
              <div className="tr-card" style={{ padding: 14 }}>
                <h3 className="tr-card-header" style={{ marginBottom: 12 }}>Report Output</h3>

                {simResults ? (
                  <>
                    <div className="tr-stats-grid" style={{ marginBottom: 12 }}>
                      <div className="tr-stat-card">
                        <div className="tr-stat-label">Total Over-Quota Score</div>
                        <div className="tr-stat-value">{simResults.totalScore.toFixed(2)}</div>
                      </div>
                      <div className="tr-stat-card">
                        <div className="tr-stat-label">Qualified Contributors</div>
                        <div className="tr-stat-value">{simResults.qualified}</div>
                      </div>
                      <div className="tr-stat-card">
                        <div className="tr-stat-label">Allocated Reward</div>
                        <div className="tr-stat-value">{simResults.rows.reduce((s, r) => s + r.reward, 0).toFixed(2)}</div>
                      </div>
                    </div>

                    <table className="users-table" style={{ marginBottom: 12 }}>
                      <thead>
                        <tr>
                          <th>Contributor</th>
                          <th>Weighted Done</th>
                          <th>Quota</th>
                          <th>Over Quota</th>
                          <th>Attendance</th>
                          <th>Multiplier</th>
                          <th>Final Score</th>
                          <th>Reward</th>
                        </tr>
                      </thead>
                      <tbody>
                        {simResults.rows.map((r) => {
                          const status = getSimStatusStyle(r.attendance, r.multiplier);
                          return (
                            <tr key={r.name}>
                              <td>{r.name}</td>
                              <td>{r.weightedCompleted}</td>
                              <td>{r.quota}</td>
                              <td>{r.overQuota}</td>
                              <td>
                                {r.attendance}%{' '}
                                <span style={{ ...status, borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 700, marginLeft: 6 }}>
                                  {status.label}
                                </span>
                              </td>
                              <td>{r.multiplier}x</td>
                              <td><strong>{r.finalScore}</strong></td>
                              <td><strong>{r.reward.toFixed(2)}</strong></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    <div style={{ marginTop: 12 }}>
                      {simResults.rows.map((r) => {
                        const maxReward = Math.max(1, ...simResults.rows.map((row) => row.reward));
                        const width = (r.reward / maxReward) * 100;
                        return (
                          <div key={r.name} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 70px', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{r.name}</div>
                            <div style={{ height: 12, borderRadius: 999, background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${width}%`, background: 'linear-gradient(90deg, #22d3ee, #38bdf8)' }} />
                            </div>
                            <div style={{ textAlign: 'right', fontWeight: 700 }}>{r.reward.toFixed(2)}</div>
                          </div>
                        );
                      })}
                    </div>

                    <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
                      Rules used:<br />
                      - Task weights: low=1, medium=1.5, high=2, urgent=2.<br />
                      - Quota uses weighted tasks.<br />
                      - Over-quota points = max(0, weightedCompleted - quota).<br />
                      - Attendance multiplier: ≥95%: 2x, ≥80%: 1.5x, else 1x.<br />
                      - If attendance &lt; threshold, multiplier = 0 (disqualified for reward).
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 20 }}>
                    Click "Run Simulation" to see results
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rewards;
